/**
 * Per-Product Transparency Ledger Modal
 * Everlane-style cost breakdown with GT design system
 * 
 * FINAL LOCKED LAYOUT:
 * - Header: Espresso gradient with gold "WHERE IT GOES" eyebrow
 * - Ledger rows: off-white body, uppercase labels, precise spacing
 * - Final row: "YOUR COMMISSION" in bronze with espresso rules
 * - Footer: Cormorant italic soft message
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import api from '@/lib/api';

const ProductTransparencyModal = ({ isOpen, onClose, productName, employeeId }) => {
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBreakdown = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = employeeId ? `?employee_id=${employeeId}` : '';
      const breakdownRes = await api.get(`/retail/product-breakdown/${encodeURIComponent(productName)}${params}`);
      setBreakdown(breakdownRes.data);
    } catch (err) {
      setError('Could not load breakdown');
    } finally {
      setLoading(false);
    }
  }, [productName, employeeId]);

  useEffect(() => {
    if (isOpen && productName) {
      fetchBreakdown();
    }
  }, [isOpen, productName, employeeId, fetchBreakdown]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full overflow-hidden shadow-2xl">
        {/* Header - Espresso gradient block */}
        <div className="bg-gradient-to-br from-[#3d2817] to-[#2a1810] px-6 py-8 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition"
          >
            <X className="w-5 h-5" />
          </button>
          
          {/* Gold uppercase eyebrow */}
          <div className="text-[#d4a574] text-xs font-bold tracking-[0.15em] uppercase mb-3">
            WHERE IT GOES
          </div>
          
          {/* Product name in Cormorant ivory */}
          <h2 className="font-['Cormorant_Garamond'] text-[26px] font-semibold text-[#f5f5f0] leading-tight mb-2">
            {productName}
          </h2>
          
          {/* Price in Cormorant gold */}
          {breakdown && (
            <div className="font-['Cormorant_Garamond'] text-[20px] text-[#d4a574]">
              ${breakdown.price.toFixed(2)}
            </div>
          )}
        </div>

        {/* Ledger rows - off-white body */}
        <div className="bg-[#faf9f6] px-6">
          {loading && (
            <div className="py-12 text-center text-[#5a4a3a]">
              Loading breakdown...
            </div>
          )}

          {error && (
            <div className="py-12 text-center text-red-700">
              {error}
            </div>
          )}

          {breakdown && !loading && (
            <>
              {/* Wholesale cost row (hide if no data) */}
              {breakdown.has_cost_data && breakdown.breakdown.wholesale_cost !== null && (
                <div className="flex justify-between items-center py-[17px] border-b border-[#e8e4dc]">
                  <span className="text-xs font-bold tracking-[0.1em] uppercase text-[#3d2817]">
                    WHOLESALE COST
                  </span>
                  <span className="text-[15px] text-[#2a1810]">
                    ${breakdown.breakdown.wholesale_cost.toFixed(2)}
                  </span>
                </div>
              )}

              {/* Freight & handling row */}
              <div className="flex justify-between items-center py-[17px] border-b border-[#e8e4dc]">
                <span className="text-xs font-bold tracking-[0.1em] uppercase text-[#3d2817]">
                  FREIGHT & HANDLING
                </span>
                <span className="text-[15px] text-[#2a1810]">
                  ${breakdown.breakdown.freight_handling.toFixed(2)}
                </span>
              </div>

              {/* Credit card fees row */}
              <div className="flex justify-between items-center py-[17px] border-b border-[#e8e4dc]">
                <span className="text-xs font-bold tracking-[0.1em] uppercase text-[#3d2817]">
                  CREDIT CARD FEES ({breakdown.breakdown.card_fee_percent}%)
                </span>
                <span className="text-[15px] text-[#2a1810]">
                  ${breakdown.breakdown.card_fees.toFixed(2)}
                </span>
              </div>

              {/* GT remainder row - backend returns null for staff (studio's cut stays private) */}
              {breakdown.has_cost_data && breakdown.breakdown.gt_remainder !== null && (
                <div className="flex justify-between items-center py-[17px] border-b border-[#e8e4dc]">
                  <span className="text-xs font-bold tracking-[0.1em] uppercase text-[#3d2817]">
                    GT — NEXT BULK ORDER
                  </span>
                  <span className="text-[15px] text-[#2a1810]">
                    ${breakdown.breakdown.gt_remainder.toFixed(2)}
                  </span>
                </div>
              )}

              {breakdown.breakdown.your_commission != null && (<>
              {/* YOUR COMMISSION - final row with espresso rules */}
              <div className="py-6 my-4 border-t-2 border-b-2 border-[#2a1810]">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium tracking-wide text-[#b8860b]">
                    YOUR COMMISSION
                  </span>
                  <span className="font-['Cormorant_Garamond'] text-[26px] font-semibold text-[#b8860b]">
                    ${breakdown.breakdown.your_commission.toFixed(2)}
                  </span>
                </div>
              </div>
              </>)}

              {/* Footer message */}
              <div className="pt-4 pb-6">
                <p className="font-['Cormorant_Garamond'] italic text-[14.5px] text-[#5a4a3a] text-center leading-relaxed">
                  {breakdown.footer_message}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductTransparencyModal;
